import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ArrowDown } from "components/icons/ArrowDown";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { MagicWand } from "components/icons/MagicWand";
import { PlusIcon } from "components/icons/PlusIcon";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import dayjs from "dayjs";
import { ReconciliationStartDateModal } from "./ReconciliationStartDateModal";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { colors } from "constants/colors";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import { useHistory } from "react-router-dom";

type ReconcileButtonProps = {
  bankAccountId: string;
  entityId: string;
  lastReconciledDate?: string | null;
  reconciliationStartDate?: string | null;
  parentPage: "all" | "history";
  hasWritePermission: boolean;
  // Stripe accounts are reconciled only by uploading the Stripe balance summary
  isStripe?: boolean;
};

export const ReconcileButton = ({
  bankAccountId,
  entityId,
  lastReconciledDate,
  reconciliationStartDate,
  parentPage,
  hasWritePermission,
  isStripe = false,
  size = "small",
  customType,
  ...rest
}: ReconcileButtonProps & ButtonProps) => {
  const {
    isOpen: isStartDateModalOpen,
    open: openStartDateModal,
    close: closeStartDateModal,
  } = useModal();

  const { link } = useConstructInternalLink();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const history = useHistory();

  const buildReconcileRoute = (moreQuery?: Record<string, string>) => {
    const path =
      parentPage === "history"
        ? `/books/reconciliation/history/${bankAccountId}/start/${bankAccountId}`
        : `/books/reconciliation/start/${bankAccountId}`;

    return link(path, moreQuery ? { moreQuery } : undefined);
  };

  if (!hasWritePermission) {
    return (
      <div className="t-w-fit">
        <LockedButton text="Reconcile" />
      </div>
    );
  }

  if (!reconciliationStartDate) {
    return (
      <>
        <Button
          size={size}
          customType="secondary"
          leftIcon={<CalendarIcon />}
          onClick={openStartDateModal}
          {...rest}
        >
          Set start date
        </Button>
        <ReconciliationStartDateModal
          isOpen={isStartDateModalOpen}
          onClose={closeStartDateModal}
          accountId={bankAccountId}
          entityId={entityId}
        />
      </>
    );
  }

  // Condition 4: last_reconciled_date exists and equals current date (fully reconciled)
  if (lastReconciledDate && dayjs(lastReconciledDate).isSame(dayjs(), "day")) {
    return (
      <ConditionalToolTip
        condition="You're fully reconciled as of today. There's nothing new to reconcile."
        side="top"
      >
        <span>
          <Button size={size} disabled {...rest}>
            Reconcile
          </Button>
        </span>
      </ConditionalToolTip>
    );
  }

  // Stripe accounts reconcile only by uploading the Stripe balance summary
  if (isStripe) {
    return (
      <Button
        size={size}
        customType={customType}
        onClick={() => history.push(buildReconcileRoute({ mode: "ai" }))}
        {...rest}
      >
        Reconcile
      </Button>
    );
  }

  // Condition 2: No last_reconciled_date but reconciliation_start_date exists - enabled
  // Condition 3: last_reconciled_date exists and is before current date - enabled
  const isEnabled =
    (!lastReconciledDate && reconciliationStartDate) ||
    (lastReconciledDate && dayjs(lastReconciledDate).isBefore(dayjs(), "day"));

  const startDateRoute = isEnabled ? buildReconcileRoute() : "";
  const aiReconcileRoute = isEnabled ? buildReconcileRoute({ mode: "ai" }) : "";

  const handleSmartReconcile = () => {
    if (aiReconcileRoute) {
      history.push(aiReconcileRoute);
    }
  };

  const handleManualReconcile = () => {
    if (startDateRoute) {
      history.push(startDateRoute);
    }
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group t-text-text-60">
        <div>
          <Button
            size={size}
            disabled={!isEnabled}
            customType={customType}
            {...rest}
          >
            Reconcile
            <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-ml-1.5">
              <ArrowDown color="currentColor" strokeWidth="1.5" />
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={8}
          side="bottom"
          className="t-min-w-48"
          align="start"
        >
          {(isCustomer || isAdmin) && (
            <Dropdown.Item
              className="t-px-3 t-py-2 t-w-full"
              onSelect={handleSmartReconcile}
              icon={<MagicWand color={colors.text.black} />}
            >
              <div className="t-flex t-items-center t-justify-between t-w-full">
                <div className="t-flex t-items-center t-gap-2">
                  <span>Smart reconcile</span>
                </div>

                <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-body-sm t-px-2 t-rounded-full">
                  <img
                    src={AIGenerateButtonStar}
                    alt="AI star"
                    className="t-w-3 t-h-3"
                  />
                  AI
                </div>
              </div>
            </Dropdown.Item>
          )}
          <Dropdown.Item
            onSelect={handleManualReconcile}
            icon={
              <span className="t-text-text-30">
                <PlusIcon size="12" />
              </span>
            }
          >
            Reconcile manually
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
